import type {MCFetcher} from '@github-ui/ahp-relay/mission-control/mission-control-environment-client'
import {
  clearActivationCorrelations,
  getActivationCorrelation,
  recordActivationCorrelation,
} from '@github-ui/copilot-browser-latency/activation-correlation'
import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {sendDatadogRumTelemetryAction} from '@github-ui/stats'

const ACTIVATION_TRACE_ID_PATTERN = /^[0-9a-f]{32}$/i
const DATADOG_TRACE_BASE_URL = 'https://app.datadoghq.com/apm/trace/'
const ACTIVATION_TRACE_STORAGE_KEY = 'github.activation-trace-ids'
const EMPTY_TRACE_ID = ''
const MAX_TASKS = 5
const ACTIVATION_TRACEPARENT_HEADER = 'X-Copilot-Activation-Traceparent'
const ACTIVATION_SEQUENCE_HEADER = 'X-Copilot-Activation-Sequence'
const ACTIVATION_TRACESTATE_HEADER = 'X-Copilot-Activation-Tracestate'

const traceIdsByTask = new Map<string, string>()
const connectionTraceIdsByTask = new Map<string, string>()
const listenersByTask = new Map<string, Set<() => void>>()

interface StoredActivationTrace {
  taskId: string
  traceId: string
  sequence?: string
  source?: ActivationTraceSource
  sessionId?: string
  environmentId?: string
}

export interface ActivationTraceContext {
  readonly version?: number
  readonly sequence?: string
  readonly traceparent: string
  readonly tracestate?: string
  readonly traceId: string
}

export interface ActivationTraceIdentity {
  readonly taskId: string
  readonly sessionId?: string
  readonly environmentId?: string
}

type ActivationTraceSource = 'connect' | 'reconnect' | 'unknown'

function getSessionStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

function readStoredActivationTraces(): StoredActivationTrace[] {
  const storage = getSessionStorage()
  if (!storage) return []

  try {
    const value: unknown = JSON.parse(storage.getItem(ACTIVATION_TRACE_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(value)) return []
    return value
      .filter(
        (entry): entry is StoredActivationTrace =>
          !!entry &&
          typeof entry === 'object' &&
          'taskId' in entry &&
          typeof entry.taskId === 'string' &&
          entry.taskId.length > 0 &&
          'traceId' in entry &&
          typeof entry.traceId === 'string' &&
          ACTIVATION_TRACE_ID_PATTERN.test(entry.traceId) &&
          (!('sequence' in entry) || entry.sequence === undefined || typeof entry.sequence === 'string') &&
          (!('source' in entry) ||
            entry.source === undefined ||
            entry.source === 'connect' ||
            entry.source === 'reconnect' ||
            entry.source === 'unknown') &&
          (!('sessionId' in entry) || entry.sessionId === undefined || typeof entry.sessionId === 'string') &&
          (!('environmentId' in entry) || entry.environmentId === undefined || typeof entry.environmentId === 'string'),
      )
      .slice(-MAX_TASKS)
  } catch {
    return []
  }
}

function storedActivationTraceId(taskId: string): string | undefined {
  const traces = readStoredActivationTraces()
  for (let index = traces.length - 1; index >= 0; index--) {
    if (traces[index]?.taskId === taskId) return traces[index]?.traceId
  }
  return undefined
}

function storeActivationTrace(
  identity: ActivationTraceIdentity,
  context: ActivationTraceContext,
  source: ActivationTraceSource,
): void {
  const storage = getSessionStorage()
  if (!storage) return

  const traces = readStoredActivationTraces().filter(trace => trace.taskId !== identity.taskId)
  traces.push({
    taskId: identity.taskId,
    traceId: context.traceId,
    ...(context.sequence ? {sequence: context.sequence} : {}),
    source,
    ...(identity.sessionId ? {sessionId: identity.sessionId} : {}),
    ...(identity.environmentId ? {environmentId: identity.environmentId} : {}),
  })
  try {
    storage.setItem(ACTIVATION_TRACE_STORAGE_KEY, JSON.stringify(traces.slice(-MAX_TASKS)))
  } catch {
    // The in-memory trace remains available when same-tab handoff storage is unavailable.
  }
}

function isBrowserTelemetryEnabled(): boolean {
  return isFeatureEnabled('one_copilot_chat')
}

if (!IS_SERVER && isBrowserTelemetryEnabled()) {
  for (const stored of readStoredActivationTraces()) {
    recordActivationCorrelation({
      task_id: stored.taskId,
      ...(stored.sessionId ? {session_id: stored.sessionId} : {}),
      ...(stored.environmentId ? {environment_id: stored.environmentId} : {}),
      activation_trace_id: stored.traceId,
      ...(stored.sequence ? {activation_sequence: stored.sequence} : {}),
      activation_source: stored.source ?? 'unknown',
    })
  }
}

export function getActivationTraceIdSnapshot(taskId: string | undefined): string {
  if (!taskId) return EMPTY_TRACE_ID
  return traceIdsByTask.get(taskId) ?? storedActivationTraceId(taskId) ?? EMPTY_TRACE_ID
}

export function getConnectionTraceIdSnapshot(taskId: string | undefined): string {
  return taskId ? (connectionTraceIdsByTask.get(taskId) ?? EMPTY_TRACE_ID) : EMPTY_TRACE_ID
}

function notifyTraceListeners(taskId: string): void {
  for (const listener of listenersByTask.get(taskId) ?? []) {
    try {
      listener()
    } catch {
      // Diagnostic subscribers must not interrupt startup or other subscribers.
    }
  }
}

function recordConnectionTraceparent(taskId: string, traceparent: unknown): void {
  if (!isFeatureEnabled('one_copilot_connection_tracing')) return
  const traceId = parseTraceIdFromTraceparent(traceparent)
  if (!traceId || connectionTraceIdsByTask.get(taskId) === traceId) return
  connectionTraceIdsByTask.delete(taskId)
  connectionTraceIdsByTask.set(taskId, traceId)
  for (const staleTaskId of [...connectionTraceIdsByTask.keys()].slice(0, -MAX_TASKS)) {
    connectionTraceIdsByTask.delete(staleTaskId)
    notifyTraceListeners(staleTaskId)
  }
  notifyTraceListeners(taskId)
}

export function subscribeToActivationTraceId(taskId: string | undefined, listener: () => void): () => void {
  if (!taskId) return () => {}
  const listeners = listenersByTask.get(taskId) ?? new Set<() => void>()
  listeners.add(listener)
  listenersByTask.set(taskId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersByTask.delete(taskId)
  }
}

export function recordActivationTraceId(taskId: string, traceId: unknown): void {
  if (typeof traceId !== 'string' || !ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return
  recordActivationTrace(
    {taskId},
    {traceparent: `00-${traceId.toLowerCase()}-0000000000000001-00`, traceId: traceId.toLowerCase()},
    'unknown',
  )
}

function recordActivationTrace(
  identity: ActivationTraceIdentity,
  context: ActivationTraceContext,
  source: ActivationTraceSource,
): void {
  const {taskId} = identity
  const telemetryEnabled = isBrowserTelemetryEnabled()
  const previousActivation = telemetryEnabled ? getActivationCorrelation(taskId) : undefined
  storeActivationTrace(identity, context, source)
  if (telemetryEnabled) {
    recordActivationCorrelation({
      task_id: taskId,
      ...(identity.sessionId ? {session_id: identity.sessionId} : {}),
      ...(identity.environmentId ? {environment_id: identity.environmentId} : {}),
      activation_trace_id: context.traceId,
      ...(context.sequence ? {activation_sequence: context.sequence} : {}),
      activation_source: source,
    })
  }
  if (traceIdsByTask.get(taskId) === context.traceId) return
  traceIdsByTask.delete(taskId)
  traceIdsByTask.set(taskId, context.traceId)

  for (const staleTaskId of [...traceIdsByTask.keys()].slice(0, Math.max(0, traceIdsByTask.size - MAX_TASKS))) {
    if (!listenersByTask.has(staleTaskId)) traceIdsByTask.delete(staleTaskId)
  }

  notifyTraceListeners(taskId)

  if (!telemetryEnabled || previousActivation?.activation_trace_id === context.traceId) return
  sendDatadogRumTelemetryAction('one_copilot.web.activation_context', {
    task_id: taskId,
    ...(identity.sessionId ? {session_id: identity.sessionId} : {}),
    ...(identity.environmentId ? {environment_id: identity.environmentId} : {}),
    activation_trace_id: context.traceId,
    ...(context.sequence ? {activation_sequence: context.sequence} : {}),
    ...(context.version !== undefined ? {activation_version: context.version} : {}),
    activation_source: source,
  })
}

const TRACEPARENT_VERSION = '00'
const TRACEPARENT_TRACE_ID_PATTERN = /^[0-9a-f]{32}$/i
const TRACEPARENT_PARENT_ID_PATTERN = /^[0-9a-f]{16}$/i
const TRACEPARENT_FLAGS_PATTERN = /^[0-9a-f]{2}$/i
const ALL_ZERO_TRACE_ID = /^0+$/
const CAPTURE_STATUSES = new Set([200, 202, 409])

export function parseTraceIdFromTraceparent(traceparent: unknown): string | undefined {
  if (typeof traceparent !== 'string') return undefined
  const parts = traceparent.split('-')
  if (parts.length !== 4) return undefined
  const [version, traceId, parentId, flags] = parts as [string, string, string, string]
  if (version !== TRACEPARENT_VERSION) return undefined
  if (!TRACEPARENT_TRACE_ID_PATTERN.test(traceId) || ALL_ZERO_TRACE_ID.test(traceId)) return undefined
  if (!TRACEPARENT_PARENT_ID_PATTERN.test(parentId) || ALL_ZERO_TRACE_ID.test(parentId)) return undefined
  if (!TRACEPARENT_FLAGS_PATTERN.test(flags)) return undefined
  return traceId.toLowerCase()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

// activation_context reflects the current activation across task creation and environment startup.
export function activationTraceContextFromResponseBody(body: unknown): ActivationTraceContext | undefined {
  if (!isRecord(body)) return undefined
  const activationContext = body.activation_context
  return activationTraceContext(activationContext)
}

export function activationTraceIdFromResponseBody(body: unknown): string | undefined {
  return activationTraceContextFromResponseBody(body)?.traceId
}

export function activationTraceContextFromResponseHeaders(headers: Headers): ActivationTraceContext | undefined {
  const traceparent = headers.get(ACTIVATION_TRACEPARENT_HEADER)
  if (!traceparent) return undefined
  const sequence = headers.get(ACTIVATION_SEQUENCE_HEADER)
  const tracestate = headers.get(ACTIVATION_TRACESTATE_HEADER)
  return activationTraceContext({
    version: 2,
    ...(sequence ? {sequence} : {}),
    traceparent,
    ...(tracestate ? {tracestate} : {}),
  })
}

function activationTraceContext(value: unknown): ActivationTraceContext | undefined {
  if (!isRecord(value)) return undefined
  const traceId = parseTraceIdFromTraceparent(value.traceparent)
  if (!traceId || typeof value.traceparent !== 'string') return undefined
  const sequence =
    typeof value.sequence === 'string' && /^[1-9][0-9]{0,19}$/.test(value.sequence)
      ? value.sequence
      : typeof value.sequence === 'number' && Number.isSafeInteger(value.sequence) && value.sequence > 0
        ? String(value.sequence)
        : undefined
  return {
    ...(typeof value.version === 'number' && Number.isInteger(value.version) ? {version: value.version} : {}),
    ...(sequence ? {sequence} : {}),
    traceparent: value.traceparent,
    ...(typeof value.tracestate === 'string' ? {tracestate: value.tracestate} : {}),
    traceId,
  }
}

function activationTraceContextFromError(error: unknown): ActivationTraceContext | undefined {
  return error instanceof FetchCAPIError ? activationTraceContext(error.activationContext) : undefined
}

export function activationTraceIdFromError(error: unknown): string | undefined {
  return activationTraceContextFromError(error)?.traceId
}

function activationTraceSource(path: string): ActivationTraceSource {
  const pathname = path.split('?', 1)[0]
  if (pathname?.endsWith('/reconnect')) return 'reconnect'
  if (pathname?.endsWith('/connect')) return 'connect'
  return 'unknown'
}

async function captureActivationTraceId(
  identity: ActivationTraceIdentity,
  source: ActivationTraceSource,
  response: Response,
): Promise<void> {
  if (source !== 'unknown') {
    recordConnectionTraceparent(identity.taskId, response.headers.get('X-Copilot-Connection-Traceparent'))
  }
  if (!CAPTURE_STATUSES.has(response.status) && response.status < 400) return

  let context = activationTraceContextFromResponseHeaders(response.headers)
  try {
    const body: unknown = await response.clone().json()
    context = activationTraceContextFromResponseBody(body) ?? context
  } catch {
    // Header propagation remains available when a response has no JSON carrier.
  }

  if (context) recordActivationTrace(identity, context, source)
}

async function captureActivationTraceIdWithoutAffectingRequest(
  identity: ActivationTraceIdentity,
  source: ActivationTraceSource,
  response: Response,
): Promise<void> {
  try {
    await captureActivationTraceId(identity, source, response)
  } catch {
    // Trace capture is diagnostic only and must never replace the relay result.
  }
}

export function createActivationTraceCapturingFetcher(
  fetcher: MCFetcher,
  identity: ActivationTraceIdentity,
): MCFetcher {
  return async (path, options) => {
    const source = activationTraceSource(path)
    try {
      const response = await fetcher(path, options)
      await captureActivationTraceIdWithoutAffectingRequest(identity, source, response)
      return response
    } catch (error) {
      try {
        if (source !== 'unknown' && error instanceof FetchCAPIError) {
          recordConnectionTraceparent(identity.taskId, error.connectionTraceparent)
          if (error.response) {
            recordConnectionTraceparent(identity.taskId, error.response.headers.get('X-Copilot-Connection-Traceparent'))
          }
        }
        const context = activationTraceContextFromError(error)
        if (context) {
          recordActivationTrace(identity, context, source)
        } else if (error instanceof FetchCAPIError && error.response) {
          await captureActivationTraceIdWithoutAffectingRequest(identity, source, error.response)
        }
      } catch {
        // Diagnostic failures must not replace the original request error.
      }
      throw error
    }
  }
}

export function activationTraceUrl(traceId: string): string | undefined {
  if (!ACTIVATION_TRACE_ID_PATTERN.test(traceId)) return undefined
  const url = new URL(encodeURIComponent(traceId), DATADOG_TRACE_BASE_URL)
  return url.toString()
}

export function clearActivationTraceIds(): void {
  const taskIds = new Set([
    ...traceIdsByTask.keys(),
    ...connectionTraceIdsByTask.keys(),
    ...readStoredActivationTraces().map(trace => trace.taskId),
  ])
  traceIdsByTask.clear()
  connectionTraceIdsByTask.clear()
  clearActivationCorrelations()
  try {
    getSessionStorage()?.removeItem(ACTIVATION_TRACE_STORAGE_KEY)
  } catch {
    // Test and cleanup helpers remain best effort when browser storage is unavailable.
  }
  for (const taskId of taskIds) {
    for (const listener of listenersByTask.get(taskId) ?? []) listener()
  }
}
